import { debounce } from './theme.js';

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  connectedCallback() {
    this._onPopStateSortLabel = () => this.syncToolbarSortLabel();
    window.addEventListener('popstate', this._onPopStateSortLabel);
    queueMicrotask(() => this.syncToolbarSortLabel());

    this._onDocPointerDownCustomSort = (e) => this.handleCustomSortOutsideClick(e);
    document.addEventListener('pointerdown', this._onDocPointerDownCustomSort);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopStateSortLabel);
    document.removeEventListener('pointerdown', this._onDocPointerDownCustomSort);
  }

  handleCustomSortOutsideClick(event) {
    this.querySelectorAll('[data-custom-sort][data-open="true"]').forEach((wrap) => {
      if (!wrap.contains(event.target)) this.closeCustomSort(wrap);
    });
  }

  openCustomSort(wrap) {
    wrap.setAttribute('data-open', 'true');
    const toggle = wrap.querySelector('[data-custom-sort-toggle]');
    const panel = wrap.querySelector('[data-custom-sort-panel]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
  }

  closeCustomSort(wrap) {
    wrap.removeAttribute('data-open');
    const toggle = wrap.querySelector('[data-custom-sort-toggle]');
    const panel = wrap.querySelector('[data-custom-sort-panel]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  }

  selectCustomSortOption(wrap, value) {
    const select = wrap.querySelector('select[name="sort_by"]');
    if (!select) return;
    if (select.value === value) {
      this.closeCustomSort(wrap);
      return;
    }
    select.value = value;
    wrap.querySelectorAll('[data-custom-sort-panel] .facet-filters__sort-option').forEach((opt) => {
      const isSel = opt.dataset.value === value;
      opt.classList.toggle('is-selected', isSel);
      opt.setAttribute('aria-selected', isSel ? 'true' : 'false');
    });
    this.closeCustomSort(wrap);
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Toolbar sort pill: show the active option name only when `sort_by` is present in the URL;
   * otherwise show the default "Sort by" string (matches removing sort from the query).
   */
  syncToolbarSortLabel = () => {
    const sectionId = this.dataset.section;
    if (!sectionId) return;

    const span = this.querySelector(`#sort-by-${sectionId} [data-toolbar-sort-label]`);
    const select = this.querySelector(`#SortByToolbar-${sectionId}`);
    if (!span || !select) return;

    const defaultLabel = span.dataset.defaultSortLabel || '';
    const sortParam = new URLSearchParams(window.location.search).get('sort_by');

    if (sortParam == null || sortParam === '') {
      span.textContent = defaultLabel;
      return;
    }

    const match = Array.from(select.options).find((opt) => opt.value === sortParam);
    span.textContent = match ? match.textContent.trim() : defaultLabel;
  };

  onClickHandler = (event) => {
    const sortToggle = event.target.closest('[data-custom-sort-toggle]');
    if (sortToggle && this.contains(sortToggle)) {
      event.preventDefault();
      const wrap = sortToggle.closest('[data-custom-sort]');
      if (!wrap) return;
      if (wrap.getAttribute('data-open') === 'true') {
        this.closeCustomSort(wrap);
      } else {
        this.querySelectorAll('[data-custom-sort][data-open="true"]').forEach((w) => {
          if (w !== wrap) this.closeCustomSort(w);
        });
        this.openCustomSort(wrap);
      }
      return;
    }

    const sortOption = event.target.closest('[data-custom-sort-panel] .facet-filters__sort-option');
    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      const wrap = sortOption.closest('[data-custom-sort]');
      if (!wrap) return;
      const value = sortOption.dataset.value || '';
      this.selectCustomSortOption(wrap, value);
      return;
    }

    const filtersToggle = event.target.closest('[data-toggle-filters-sidebar]');
    if (filtersToggle && this.contains(filtersToggle)) {
      event.preventDefault();
      const panelId = filtersToggle.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : this.querySelector('.filters--container.filters-sidebar');
      if (!panel) return;
      const nowCollapsed = panel.classList.toggle('filters-sidebar--collapsed');
      filtersToggle.setAttribute('aria-expanded', nowCollapsed ? 'false' : 'true');
      return;
    }

    const renderSectionUrlEl = event.target.closest('[data-render-section-url]');
    if (renderSectionUrlEl && this.contains(renderSectionUrlEl)) {
      event.preventDefault();
      const raw = renderSectionUrlEl.dataset.renderSectionUrl || '';
      const queryString = raw.includes('?') ? raw.split('?').slice(1).join('?') : '';
      const searchParams = new URLSearchParams(queryString).toString();
      this.fetchSection(searchParams);
    }
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const existingParams = new URLSearchParams(window.location.search);
    const qValue = existingParams.get('q');

    this.removeDefaultPriceFilters(searchParams, form);

    const finalParams = qValue ? `q=${encodeURIComponent(qValue)}&${searchParams}` : searchParams.toString();
    this.fetchSection(finalParams);
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
    });
  };

  _setDisplay = (selector, value) => {
    const el = this.querySelector(selector);
    if (el) el.style.display = value;
  };

  showLoadingOverlay = () => {
    const s = this.dataset.section;
    this._setDisplay(`#loading-overlay-${s}`, 'flex');
    this._setDisplay(`#loading-spinner-${s}`, 'block');
    this._setDisplay(`#results-count-${s}`, 'none');
    this._setDisplay(`#drawer-results-count-${s}`, 'none');
    this._setDisplay(`#drawer-loading-spinner-${s}`, 'block');
  };

  hideLoadingOverlay = () => {
    const s = this.dataset.section;
    this._setDisplay(`#loading-overlay-${s}`, 'none');
    this._setDisplay(`#loading-spinner-${s}`, 'none');
    this._setDisplay(`#results-count-${s}`, 'block');
    this._setDisplay(`#drawer-results-count-${s}`, 'block');
    this._setDisplay(`#drawer-loading-spinner-${s}`, 'none');
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-active-filters-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateFilters(html, `js-filter`);
        this.hideLoadingOverlay();
        this.syncToolbarSortLabel();
        this.scrollToProductGrid();
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };

  scrollToProductGrid = () => {
    const productGrid = this.querySelector(`#product-grid-${this.dataset.section}`);
    const headerId = document.querySelector('#main-header');
    const headerOffset = (headerId.offsetHeight + 10) || 80;

    if (productGrid) {
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}